import { lazyWithRetry } from "@/utils/lazyWithRetry";
import { PageLoader } from "@/components/ui/page-loader";
const LazyToasters = lazyWithRetry(() => import("@/components/LazyToasters"));
import { TooltipProvider } from "@/components/ui/tooltip";
import { CATEGORY_PATHS, LOCATION_PAGE_ROUTES } from "@/config/categoryRoutes";
import { AuthProvider } from "@/contexts/AuthContext";
import { CookieConsentProvider } from "@/contexts/CookieConsentContext";
import { SupplierPageProvider } from "@/contexts/SupplierPageContext";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { lazy, Suspense } from "react";
import { HelmetProvider } from "react-helmet-async";
import { BrowserRouter, Navigate, Route, Routes, useParams } from "react-router-dom";

// Keep core navigation components synchronous
import FormRedirect from "./components/FormRedirect";
import { ScrollToTopOnNavigate } from "./components/ScrollToTopOnNavigate";
import NotFound from "./pages/website/NotFound";

// Lazy load homepage for smaller initial bundle
import Index from "./pages/website/Index";

// Lazy load components not needed for initial page render
const ChecklistPage = lazyWithRetry(() => import("./pages/website/ChecklistPage"));
const PlannerLayout = lazyWithRetry(() => import("./pages/planner/PlannerLayout"));
const ContactSideButton = lazyWithRetry(() => import("./components/ContactSideButton").then(m => ({ default: m.ContactSideButton })));
const ProtectedRoute = lazyWithRetry(() => import("./pages/blog-cms/components/auth/ProtectedRoute").then(m => ({ default: m.ProtectedRoute })));
const CookieBanner = lazyWithRetry(() => import("./components/cookies").then(m => ({ default: m.CookieBanner })));

// Lazy load all content pages
const Match = lazyWithRetry(() => import("./pages/website/Match"));
const SearchResultsPage = lazyWithRetry(() => import("./pages/website/SearchResultsPage"));
const FormPage = lazyWithRetry(() => import("./pages/website/FormPage"));
const DynamicFormPage = lazyWithRetry(() => import("./pages/website/DynamicFormPage"));
const FormStep2 = lazyWithRetry(() => import("./pages/website/FormStep2"));
const FormStep3 = lazyWithRetry(() => import("./pages/website/FormStep3"));
const FormSuccess = lazyWithRetry(() => import("./pages/website/FormSuccess"));
const SupplierRegister = lazyWithRetry(() => import("./pages/website/SupplierRegister"));
const SupplierLanding = lazyWithRetry(() => import("./pages/website/SupplierLanding"));
const SupplierCreateAccountPage = lazyWithRetry(() => import("./pages/website/SupplierCreateAccountPage"));
const Pricing = lazyWithRetry(() => import("./pages/website/Pricing"));
const SupplierCategoryLanding = lazyWithRetry(() => import("./pages/website/SupplierCategoryLanding"));
const SupplierProfile = lazyWithRetry(() => import("./pages/website/SupplierProfile"));
const AnnonsPage = lazyWithRetry(() => import("./pages/website/AnnonsPage"));
const WeddingLocations = lazyWithRetry(() => import("./pages/website/WeddingLocations"));

// Hitta location pages
const HittaPhotographers = lazyWithRetry(() => import("./pages/website/hitta/HittaPhotographers"));
const HittaVideographers = lazyWithRetry(() => import("./pages/website/hitta/HittaVideographers"));
const HittaWeddingDresses = lazyWithRetry(() => import("./pages/website/hitta/HittaWeddingDresses"));
const HittaMenswear = lazyWithRetry(() => import("./pages/website/hitta/HittaMenswear"));
const HittaMakeup = lazyWithRetry(() => import("./pages/website/hitta/HittaMakeup"));
const HittaHairdresser = lazyWithRetry(() => import("./pages/website/hitta/HittaHairdresser"));
const HittaWeddingRings = lazyWithRetry(() => import("./pages/website/hitta/HittaWeddingRings"));
const HittaWeddingCakes = lazyWithRetry(() => import("./pages/website/hitta/HittaWeddingCakes"));
const HittaCatering = lazyWithRetry(() => import("./pages/website/hitta/HittaCatering"));
const HittaBridalBouquets = lazyWithRetry(() => import("./pages/website/hitta/HittaBridalBouquets"));
const HittaMusic = lazyWithRetry(() => import("./pages/website/hitta/HittaMusic"));
const HittaEntertainment = lazyWithRetry(() => import("./pages/website/hitta/HittaEntertainment"));
const HittaWeddingPlanners = lazyWithRetry(() => import("./pages/website/hitta/HittaWeddingPlanners"));
const HittaWeddingCards = lazyWithRetry(() => import("./pages/website/hitta/HittaWeddingCards"));
const HittaUthyrning = lazyWithRetry(() => import("./pages/website/hitta/HittaUthyrning"));
const HittaBrollopsbilar = lazyWithRetry(() => import("./pages/website/hitta/HittaBrollopsbilar"));
const VenueDetails = lazyWithRetry(() => import("./pages/website/VenueDetails"));
const VenueReviews = lazyWithRetry(() => import("./pages/website/VenueReviews"));
const VenueRedirect = lazyWithRetry(() => import("./components/VenueRedirect"));
const BlogRedirect = lazyWithRetry(() => import("./components/BlogRedirect"));

// Redirect component for old /artiklar/:categorySlug/:slug URLs to /inspiration/:categorySlug/:slug
const ArticleRedirect = () => {
  const params = useParams<{ categorySlug: string; slug: string }>();
  return <Navigate to={`/inspiration/${params.categorySlug}/${params.slug}`} replace />;
};

// Redirect old /foretagsartiklar URLs to /anslut-ditt-foretag/foretagsartiklar
const ForetagsartiklarListRedirect = () => <Navigate to="/anslut-ditt-foretag/foretagsartiklar" replace />;
const ForetagsartiklarPostRedirect = () => {
  const params = useParams<{ categorySlug: string; slug: string }>();
  return <Navigate to={`/anslut-ditt-foretag/foretagsartiklar/${params.categorySlug}/${params.slug}`} replace />;
};

const MyConversations = lazyWithRetry(() => import("./pages/website/MyConversations"));
const ConversationDetail = lazyWithRetry(() => import("./pages/website/ConversationDetail"));
const SupplierConversation = lazyWithRetry(() => import("./pages/website/SupplierConversation"));
const SupplierConversationDetail = lazyWithRetry(() => import("./pages/website/SupplierConversationDetail"));
const SupplierConversations = lazyWithRetry(() => import("./pages/website/SupplierConversations"));
const ResetPassword = lazyWithRetry(() => import("./pages/website/ResetPassword"));
const CompleteSignup = lazyWithRetry(() => import("./pages/website/CompleteSignup"));
const ClaimAccount = lazyWithRetry(() => import("./pages/website/ClaimAccount"));
const WaitingConfirmation = lazyWithRetry(() => import("./pages/website/WaitingConfirmation"));
const ProfileSettings = lazyWithRetry(() => import("./pages/website/ProfileSettings"));
const GiftClaim = lazyWithRetry(() => import("./pages/website/GiftClaim"));
const PrivacyPolicy = lazyWithRetry(() => import("./pages/website/PrivacyPolicy"));
const CookieSettings = lazyWithRetry(() => import("./pages/website/CookieSettings"));
const SupplierTerms = lazyWithRetry(() => import("./pages/website/SupplierTerms"));
const TermsAndConditions = lazyWithRetry(() => import("./pages/website/TermsAndConditions"));
const Articles = lazyWithRetry(() => import("./pages/website/Articles"));
const BlogPost = lazyWithRetry(() => import("./pages/website/BlogPost"));
const SupplierArticles = lazyWithRetry(() => import("./pages/website/SupplierArticles"));
const SupplierArticlePost = lazyWithRetry(() => import("./pages/website/SupplierArticlePost"));
const AuthorProfile = lazyWithRetry(() => import("./pages/website/AuthorProfile"));
const Rentals = lazyWithRetry(() => import("./pages/website/Rentals"));
const Verktyg = lazyWithRetry(() => import("./pages/website/Verktyg"));
const BudgetCalculator = lazyWithRetry(() => import("./pages/website/BudgetCalculator"));
const GuestlistPage = lazyWithRetry(() => import("./pages/website/GuestlistPage"));
const SchemaPage = lazyWithRetry(() => import("./pages/website/SchemaPage"));
const BordsplaceringPage = lazyWithRetry(() => import("./pages/website/BordsplaceringPage"));
const SavedFavoritesPage = lazyWithRetry(() => import("./pages/website/SavedFavoritesPage"));

// Embed pages
const EmbedSeating = lazyWithRetry(() => import("./pages/embed/EmbedSeating"));
const SupplierEmbedSeating = lazyWithRetry(() => import("./pages/embed/SupplierEmbedSeating"));
const SupplierEmbedPreview = lazyWithRetry(() => import("./pages/embed/SupplierEmbedPreview"));
const DemoSeatingEmbed = lazyWithRetry(() => import("./pages/embed/DemoSeatingEmbed"));

// Browse category pages
const WeddingVenues = lazyWithRetry(() => import("./pages/website/browse/WeddingVenues"));
const Brollopsbilar = lazyWithRetry(() => import("./pages/website/browse/Brollopsbilar"));
const Photographers = lazyWithRetry(() => import("./pages/website/browse/Photographers"));
const Videographers = lazyWithRetry(() => import("./pages/website/browse/Videographers"));
const WeddingCards = lazyWithRetry(() => import("./pages/website/browse/WeddingCards"));
const WeddingDresses = lazyWithRetry(() => import("./pages/website/browse/WeddingDresses"));
const Menswear = lazyWithRetry(() => import("./pages/website/browse/Menswear"));
const WeddingCakes = lazyWithRetry(() => import("./pages/website/browse/WeddingCakes"));
const BridalBouquets = lazyWithRetry(() => import("./pages/website/browse/BridalBouquets"));
const Catering = lazyWithRetry(() => import("./pages/website/browse/Catering"));
const Entertainment = lazyWithRetry(() => import("./pages/website/browse/Entertainment"));
const Fotobas = lazyWithRetry(() => import("./pages/website/browse/Fotobas"));
const Hairdresser = lazyWithRetry(() => import("./pages/website/browse/Hairdresser"));
const Music = lazyWithRetry(() => import("./pages/website/browse/Music"));
const Makeup = lazyWithRetry(() => import("./pages/website/browse/Makeup"));
const WeddingRings = lazyWithRetry(() => import("./pages/website/browse/WeddingRings"));
const Uthyrning = lazyWithRetry(() => import("./pages/website/browse/Uthyrning"));
const WeddingPlanners = lazyWithRetry(() => import("./pages/website/browse/WeddingPlanners"));
const AllSuppliersByLocation = lazyWithRetry(() => import("./pages/website/browse/AllSuppliersByLocation"));
const RingsBilder = lazyWithRetry(() => import("./pages/website/browse/RingsBilder"));
const RingDetail = lazyWithRetry(() => import("./pages/website/RingDetail"));
const DressesBilder = lazyWithRetry(() => import("./pages/website/browse/DressesBilder"));
const DressDetail = lazyWithRetry(() => import("./pages/website/DressDetail"));
const SupplierAllDresses = lazyWithRetry(() => import("./pages/website/SupplierAllDresses"));
const SupplierAllRings = lazyWithRetry(() => import("./pages/website/SupplierAllRings"));
const ChurchDetails = lazyWithRetry(() => import("./pages/website/ChurchDetails"));
const ChurchesDiscovery = lazyWithRetry(() => import("./pages/website/ChurchesDiscovery"));
const ChurchesByCity = lazyWithRetry(() => import("./pages/website/browse/ChurchesByCity"));
const AllChurches = lazyWithRetry(() => import("./pages/website/browse/AllChurches"));

// Admin/CMS pages
const Dashboard = lazyWithRetry(() => import("./pages/admin-dashboard/Dashboard"));
const FeatureLabelsPage = lazyWithRetry(() => import("./pages/admin-dashboard/FeatureLabelsPage"));
const MarketingPage = lazyWithRetry(() => import("./pages/admin-dashboard/MarketingPage"));
const SupplierPricingPage = lazyWithRetry(() => import("./pages/admin-dashboard/SupplierPricingPage"));
const AdminLogin = lazyWithRetry(() => import("./pages/blog-cms/AdminLogin"));
const BlogCms = lazyWithRetry(() => import("./pages/blog-cms/BlogCms"));
const AdminAuthors = lazyWithRetry(() => import("./pages/blog-cms/admin/Authors"));
const AdminCategories = lazyWithRetry(() => import("./pages/blog-cms/admin/Categories"));
const AdminMedia = lazyWithRetry(() => import("./pages/blog-cms/admin/Media"));
const AdminPagelocations = lazyWithRetry(() => import("./pages/blog-cms/admin/Pagelocations"));
const PostEditor = lazyWithRetry(() => import("./pages/blog-cms/admin/PostEditor"));
const AdminPosts = lazyWithRetry(() => import("./pages/blog-cms/admin/Posts"));
const AdminArchive = lazyWithRetry(() => import("./pages/blog-cms/admin/Archive"));
const AdminRedirects = lazyWithRetry(() => import("./pages/blog-cms/admin/Redirects"));
const Settings = lazyWithRetry(() => import("./pages/blog-cms/admin/Settings"));
const CtaBlockDefaults = lazyWithRetry(() => import("./pages/blog-cms/admin/CtaBlockDefaults"));
const AdminTags = lazyWithRetry(() => import("./pages/blog-cms/admin/Tags"));
const AdminChecklistTemplates = lazyWithRetry(() => import("./pages/blog-cms/admin/ChecklistTemplates"));
const SupplierTaxonomy = lazyWithRetry(() => import("./pages/blog-cms/admin/SupplierTaxonomy"));

// Planner pages
const Budget = lazyWithRetry(() => import("./pages/planner/Budget"));
const Chat = lazyWithRetry(() => import("./pages/planner/Chat"));
const Checklist = lazyWithRetry(() => import("./pages/planner/Checklist"));
const Guestlist = lazyWithRetry(() => import("./pages/planner/Guestlist"));
const Leverantorer = lazyWithRetry(() => import("./pages/planner/Leverantorer"));
const Profile = lazyWithRetry(() => import("./pages/planner/Profile"));
const Saved = lazyWithRetry(() => import("./pages/planner/Saved"));
const Schema = lazyWithRetry(() => import("./pages/planner/Schema"));
const Seating = lazyWithRetry(() => import("./pages/planner/Seating"));
const RoomsPage = lazyWithRetry(() => import("./pages/planner/RoomsPage"));
const WeddingDashboard = lazyWithRetry(() => import("./pages/planner/components/WeddingDashboard").then(m => ({ default: m.WeddingDashboard })));

// Planner V2 - Development Playground (admin-only)
const PlannerV2Layout = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Layout"));
const PlannerV2Dashboard = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Dashboard"));
const PlannerV2Checklist = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Checklist"));
const PlannerV2Schema = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Schema"));
const PlannerV2Budget = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Budget"));
const PlannerV2Guestlist = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Guestlist"));
const PlannerV2Seating = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Seating"));
const PlannerV2Settings = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Settings"));
const PlannerV2Dietary = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Dietary"));
const PlannerV2Communication = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Communication"));
const PlannerV2Chat = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Chat"));
const PlannerV2WeddingPage = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2WeddingPage"));
const PlannerV2GuestApp = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2GuestApp"));
const PlannerV2Menu = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Menu"));
const PlannerV2Gifts = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Gifts"));
const PlannerV2Wishlist = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Wishlist"));
const PlannerV2Moodboard = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Moodboard"));
const PlannerV2AiConsultant = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2AiConsultant"));
const PlannerV2VendorCatalog = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2VendorCatalog"));
const PlannerV2MyVendors = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2MyVendors"));
const PlannerV2Report = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Report"));
const PlannerV2Onboarding = lazyWithRetry(() => import("./pages/planner-v2/PlannerV2Onboarding"));
const PublicWeddingPage = lazyWithRetry(() => import("./pages/planner-v2/PublicWeddingPage"));

const queryClient = new QueryClient();

// Redirect component for old photographers/:location URLs
const PhotographersLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.PHOTOGRAPHERS}/${location}`} replace />;
};

// Redirect component for old videographers/:location URLs
const VideographersLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.VIDEOGRAPHERS}/${location}`} replace />;
};

// Redirect component for old wedding-dresses/:location URLs
const WeddingDressesLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.DRESSES}/${location}`} replace />;
};

// Redirect component for old menswear/:location URLs
const MenswearLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.MENSWEAR}/${location}`} replace />;
};

// Redirect component for old hairdresser/:location URLs
const HairdresserLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.HAIRDRESSER}/${location}`} replace />;
};

// Redirect component for old wedding-rings/:location URLs
const WeddingRingsLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.RINGS}/${location}`} replace />;
};

// Redirect component for old wedding-cakes/:location URLs
const WeddingCakesLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.CAKES}/${location}`} replace />;
};

// Redirect component for old catering/:location URLs
const CateringLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.CATERING}/${location}`} replace />;
};

// Redirect component for old bridal-bouquets/:location URLs
const BridalBouquetsLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.BOUQUETS}/${location}`} replace />;
};

// Redirect component for old music/:location URLs
const MusicLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.MUSIC}/${location}`} replace />;
};

// Redirect component for old entertainment/:location URLs
const EntertainmentLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.ENTERTAINMENT}/${location}`} replace />;
};

// Redirect component for old wedding-planners/:location URLs
const WeddingPlannersLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.PLANNERS}/${location}`} replace />;
};

// Redirect component for old wedding-cards/:location URLs
const WeddingCardsLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.CARDS}/${location}`} replace />;
};

// Redirect component for old uthyrning/:location URLs
const UthyrningLocationRedirect = () => {
  const { location } = useParams();
  return <Navigate to={`/${CATEGORY_PATHS.RENTALS}/${location}`} replace />;
};


const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <CookieConsentProvider>
          <TooltipProvider>
            <Suspense fallback={null}>
              <LazyToasters />
            </Suspense>
            <BrowserRouter>
              <Suspense fallback={null}>
                <CookieBanner />
              </Suspense>
              <ScrollToTopOnNavigate />
              <Suspense fallback={null}>
                <ContactSideButton />
              </Suspense>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Index />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/complete-signup" element={<CompleteSignup />} />
                  <Route path="/claim-account" element={<ClaimAccount />} />
                  <Route path="/waiting-confirmation" element={<WaitingConfirmation />} />
                  <Route path="/match" element={<Match />} />
                  <Route path="/sok" element={<SearchResultsPage />} />
                  <Route path="/form" element={<FormPage />} />
                  {/* Redirect from old route to new route for backward compatibility */}
                  <Route path="/form/:categorySlug" element={<FormRedirect />} />
                  <Route path="/form/:categorySlug/step1" element={<DynamicFormPage />} />
                  <Route path="/form/:categorySlug/step2" element={<FormStep2 />} />
                  <Route path="/form/:categorySlug/step3" element={<FormStep3 />} />
                  <Route path="/form/:categorySlug/success" element={<FormSuccess />} />
                  <Route path="/supplier-register" element={<SupplierRegister />} />
                  <Route path="/leverantor/intresseanmalan" element={<Navigate to="/anslut-ditt-foretag" replace />} />
                  <Route path="/annonsera-hos-oss" element={<AnnonsPage />} />
                  <Route path="/anslut-ditt-foretag" element={<SupplierLanding />} />
                  <Route path="/anslut-ditt-konto/skapa-konto" element={<SupplierCreateAccountPage />} />
                  <Route path="/anslut-ditt-foretag/priser" element={<Pricing />} />
                  <Route path="/anslut-ditt-foretag/:categorySlug" element={<SupplierCategoryLanding />} />
                  <Route path="/supplier/:id/embed-preview" element={<SupplierEmbedPreview />} />
                  <Route path="/supplier/:id" element={<SupplierPageProvider><SupplierProfile /></SupplierPageProvider>} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.VENUES_LOCATIONS}`} element={<WeddingLocations />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.PHOTOGRAPHERS_LOCATIONS}`} element={<HittaPhotographers />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.VIDEOGRAPHERS_LOCATIONS}`} element={<HittaVideographers />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.DRESSES_LOCATIONS}`} element={<HittaWeddingDresses />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.MENSWEAR_LOCATIONS}`} element={<HittaMenswear />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.MAKEUP_LOCATIONS}`} element={<HittaMakeup />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.HAIRDRESSER_LOCATIONS}`} element={<HittaHairdresser />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.RINGS_LOCATIONS}`} element={<HittaWeddingRings />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.CAKES_LOCATIONS}`} element={<HittaWeddingCakes />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.CATERING_LOCATIONS}`} element={<HittaCatering />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.BOUQUETS_LOCATIONS}`} element={<HittaBridalBouquets />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.MUSIC_LOCATIONS}`} element={<HittaMusic />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.ENTERTAINMENT_LOCATIONS}`} element={<HittaEntertainment />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.PLANNERS_LOCATIONS}`} element={<HittaWeddingPlanners />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.CARDS_LOCATIONS}`} element={<HittaWeddingCards />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.RENTALS_LOCATIONS}`} element={<HittaUthyrning />} />
                  <Route path={`/${LOCATION_PAGE_ROUTES.CARS_LOCATIONS}`} element={<HittaBrollopsbilar />} />
                  <Route path={`/${CATEGORY_PATHS.VENUES}`} element={<WeddingVenues />} />
                  <Route path={`/${CATEGORY_PATHS.VENUES}/:location`} element={<WeddingVenues />} />
                  <Route path={`/${CATEGORY_PATHS.CARS}`} element={<Brollopsbilar />} />
                  <Route path={`/${CATEGORY_PATHS.CARS}/:location`} element={<Brollopsbilar />} />
                  {/* Supplier all dresses page - must be before the generic /:category/leverantor/:slug route */}
                  <Route path="/brollopsklanningar/leverantor/:slug/alla-brollopsklanningar" element={<SupplierAllDresses />} />
                  {/* Supplier all rings page - must be before the generic /:category/leverantor/:slug route */}
                  <Route path="/vigselringar/leverantor/:slug/alla-vigselringar" element={<SupplierAllRings />} />
                  {/* New venue detail URL pattern: /{category}/leverantor/{slug} */}
                  <Route path="/:category/leverantor/:slug" element={<VenueDetails />} />
                  <Route path="/:category/leverantor/:slug/reviews" element={<VenueReviews />} />
                  {/* Church pages */}
                  <Route path="/kyrkor" element={<ChurchesDiscovery />} />
                  <Route path="/kyrkor/alla" element={<AllChurches />} />
                  <Route path="/kyrkor/:city" element={<ChurchesByCity />} />
                  <Route path="/kyrkor/:city/:slug" element={<ChurchDetails />} />
                  {/* Redirect old /venue/:slug URLs to new pattern */}
                  <Route path="/venue/:slug" element={<VenueRedirect />} />
                  <Route path="/venue/:slug/reviews" element={<VenueRedirect />} />
                  {/* SEO Redirects - Old photographers URLs */}
                  <Route path="/photographers" element={<Navigate to={`/${CATEGORY_PATHS.PHOTOGRAPHERS}`} replace />} />
                  <Route path="/photographers/:location" element={<PhotographersLocationRedirect />} />
                  <Route path="/brollopstjanster/photographers" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.PHOTOGRAPHERS_LOCATIONS}`} replace />} />

                  <Route path={`/${CATEGORY_PATHS.PHOTOGRAPHERS}`} element={<Photographers />} />
                  <Route path={`/${CATEGORY_PATHS.PHOTOGRAPHERS}/:location`} element={<Photographers />} />
                  <Route path="/photo-booths" element={<Navigate to={`/${CATEGORY_PATHS.FOTO_BAS}`} replace />} />
                  {/* SEO Redirects - Old videographers URLs */}
                  <Route path="/videographers" element={<Navigate to={`/${CATEGORY_PATHS.VIDEOGRAPHERS}`} replace />} />
                  <Route path="/videographers/:location" element={<VideographersLocationRedirect />} />
                  <Route path="/brollopstjanster/videographers" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.VIDEOGRAPHERS_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old wedding-dresses URLs */}
                  <Route path="/wedding-dresses" element={<Navigate to={`/${CATEGORY_PATHS.DRESSES}`} replace />} />
                  <Route path="/wedding-dresses/:location" element={<WeddingDressesLocationRedirect />} />
                  <Route path="/brollopstjanster/wedding-dresses" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.DRESSES_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old menswear URLs */}
                  <Route path="/menswear" element={<Navigate to={`/${CATEGORY_PATHS.MENSWEAR}`} replace />} />
                  <Route path="/menswear/:location" element={<MenswearLocationRedirect />} />
                  <Route path="/brollopstjanster/menswear" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.MENSWEAR_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old hairdresser URLs */}
                  <Route path="/hairdresser" element={<Navigate to={`/${CATEGORY_PATHS.HAIRDRESSER}`} replace />} />
                  <Route path="/hairdresser/:location" element={<HairdresserLocationRedirect />} />
                  <Route path="/brollopstjanster/hairdresser" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.HAIRDRESSER_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old wedding-rings URLs */}
                  <Route path="/wedding-rings" element={<Navigate to={`/${CATEGORY_PATHS.RINGS}`} replace />} />
                  <Route path="/wedding-rings/:location" element={<WeddingRingsLocationRedirect />} />
                  <Route path="/brollopstjanster/wedding-rings" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.RINGS_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old wedding-cakes URLs */}
                  <Route path="/wedding-cakes" element={<Navigate to={`/${CATEGORY_PATHS.CAKES}`} replace />} />
                  <Route path="/wedding-cakes/:location" element={<WeddingCakesLocationRedirect />} />
                  <Route path="/brollopstjanster/wedding-cakes" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.CAKES_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old catering URLs */}
                  <Route path="/catering" element={<Navigate to={`/${CATEGORY_PATHS.CATERING}`} replace />} />
                  <Route path="/catering/:location" element={<CateringLocationRedirect />} />
                  <Route path="/brollopstjanster/catering" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.CATERING_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old bridal-bouquets URLs */}
                  <Route path="/bridal-bouquets" element={<Navigate to={`/${CATEGORY_PATHS.BOUQUETS}`} replace />} />
                  <Route path="/bridal-bouquets/:location" element={<BridalBouquetsLocationRedirect />} />
                  <Route path="/brollopstjanster/bridal-bouquets" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.BOUQUETS_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old music URLs */}
                  <Route path="/music" element={<Navigate to={`/${CATEGORY_PATHS.MUSIC}`} replace />} />
                  <Route path="/music/:location" element={<MusicLocationRedirect />} />
                  <Route path="/brollopstjanster/music" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.MUSIC_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old entertainment URLs */}
                  <Route path="/entertainment" element={<Navigate to={`/${CATEGORY_PATHS.ENTERTAINMENT}`} replace />} />
                  <Route path="/entertainment/:location" element={<EntertainmentLocationRedirect />} />
                  <Route path="/brollopstjanster/entertainment" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.ENTERTAINMENT_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old wedding-planners URLs */}
                  <Route path="/wedding-planners" element={<Navigate to={`/${CATEGORY_PATHS.PLANNERS}`} replace />} />
                  <Route path="/wedding-planners/:location" element={<WeddingPlannersLocationRedirect />} />
                  <Route path="/brollopstjanster/wedding-planners" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.PLANNERS_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old wedding-cards URLs */}
                  <Route path="/wedding-cards" element={<Navigate to={`/${CATEGORY_PATHS.CARDS}`} replace />} />
                  <Route path="/wedding-cards/:location" element={<WeddingCardsLocationRedirect />} />
                  <Route path="/brollopstjanster/wedding-cards" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.CARDS_LOCATIONS}`} replace />} />

                  {/* SEO Redirects - Old uthyrning URLs */}
                  <Route path="/uthyrning" element={<Navigate to={`/${CATEGORY_PATHS.RENTALS}`} replace />} />
                  <Route path="/uthyrning/:location" element={<UthyrningLocationRedirect />} />
                  <Route path="/brollopstjanster/uthyrning" element={<Navigate to={`/${LOCATION_PAGE_ROUTES.RENTALS_LOCATIONS}`} replace />} />

                  <Route path={`/${CATEGORY_PATHS.VIDEOGRAPHERS}`} element={<Videographers />} />
                  <Route path={`/${CATEGORY_PATHS.VIDEOGRAPHERS}/:location`} element={<Videographers />} />
                  <Route path={`/${CATEGORY_PATHS.CARDS}`} element={<WeddingCards />} />
                  <Route path={`/${CATEGORY_PATHS.CARDS}/:location`} element={<WeddingCards />} />
                  <Route path={`/${CATEGORY_PATHS.DRESSES}`} element={<WeddingDresses />} />
                  <Route path={`/${CATEGORY_PATHS.DRESSES}/:location`} element={<WeddingDresses />} />
                  <Route path={`/${CATEGORY_PATHS.MENSWEAR}`} element={<Menswear />} />
                  <Route path={`/${CATEGORY_PATHS.MENSWEAR}/:location`} element={<Menswear />} />
                  <Route path={`/${CATEGORY_PATHS.CAKES}`} element={<WeddingCakes />} />
                  <Route path={`/${CATEGORY_PATHS.CAKES}/:location`} element={<WeddingCakes />} />
                  <Route path={`/${CATEGORY_PATHS.BOUQUETS}`} element={<BridalBouquets />} />
                  <Route path={`/${CATEGORY_PATHS.BOUQUETS}/:location`} element={<BridalBouquets />} />
                  <Route path={`/${CATEGORY_PATHS.CATERING}`} element={<Catering />} />
                  <Route path={`/${CATEGORY_PATHS.CATERING}/:location`} element={<Catering />} />
                  <Route path={`/${CATEGORY_PATHS.FOTO_BAS}`} element={<Fotobas />} />
                  <Route path={`/${CATEGORY_PATHS.FOTO_BAS}/:location`} element={<Fotobas />} />
                  <Route path={`/${CATEGORY_PATHS.ENTERTAINMENT}`} element={<Entertainment />} />
                  <Route path={`/${CATEGORY_PATHS.ENTERTAINMENT}/:location`} element={<Entertainment />} />
                  <Route path={`/${CATEGORY_PATHS.HAIRDRESSER}`} element={<Hairdresser />} />
                  <Route path={`/${CATEGORY_PATHS.HAIRDRESSER}/:location`} element={<Hairdresser />} />

                  <Route path="/my-conversations" element={<MyConversations />} />

                  <Route path="/my-conversations/:id" element={<ConversationDetail />} />
                  <Route path="/my-conversations/:id/conversation/:conversationId" element={<SupplierConversation />} />
                  <Route path="/supplier-conversations" element={<SupplierPageProvider><SupplierConversations /></SupplierPageProvider>} />
                  <Route path="/supplier-conversations/:id" element={<SupplierPageProvider><SupplierConversationDetail /></SupplierPageProvider>} />
                  <Route path="/supplier-conversations/:id/conversation/:conversationId" element={<SupplierPageProvider><SupplierConversation /></SupplierPageProvider>} />
                  {/* Legacy route redirect */}
                  <Route path="/my-conversations/:id/supplier/:supplierRequestId" element={<ConversationDetail />} />

                  {/* New category pages */}
                  <Route path={`/${CATEGORY_PATHS.MUSIC}`} element={<Music />} />
                  <Route path={`/${CATEGORY_PATHS.MUSIC}/:location`} element={<Music />} />
                  <Route path={`/${CATEGORY_PATHS.MAKEUP}`} element={<Makeup />} />
                  <Route path={`/${CATEGORY_PATHS.MAKEUP}/:location`} element={<Makeup />} />
                  <Route path={`/${CATEGORY_PATHS.RINGS}`} element={<WeddingRings />} />
                  <Route path={`/${CATEGORY_PATHS.RINGS}/:location`} element={<WeddingRings />} />
                  <Route path="/alla-vigselringar" element={<RingsBilder />} />
                  <Route path="/vigselringar" element={<Navigate to="/alla-vigselringar" replace />} />
                  <Route path="/vigselringar/bilder" element={<Navigate to="/alla-vigselringar" replace />} />
                  <Route path="/produkter/:brand/:name/:sku" element={<RingDetail />} />
                  <Route path="/produkter/:name/:sku" element={<RingDetail />} />
                  {/* Wedding Dresses Product Pages */}
                  <Route path="/alla-brollopsklanningar" element={<DressesBilder />} />
                  <Route path="/klanning/:brand/:slug" element={<DressDetail />} />
                  <Route path="/klanning/:slug" element={<DressDetail />} />
                  <Route path="/rentals" element={<Rentals />} />
                  <Route path={`/${CATEGORY_PATHS.RENTALS}`} element={<Uthyrning />} />
                  <Route path={`/${CATEGORY_PATHS.RENTALS}/:location`} element={<Uthyrning />} />
                  <Route path={`/${CATEGORY_PATHS.PLANNERS}`} element={<WeddingPlanners />} />
                  <Route path={`/${CATEGORY_PATHS.PLANNERS}/:location`} element={<WeddingPlanners />} />
                  <Route path="/leverantorer/:location" element={<AllSuppliersByLocation />} />
                  <Route path="/inspiration" element={<Articles />} />
                  <Route path="/artiklar" element={<Navigate to="/inspiration" replace />} />
                  <Route path="/verktyg" element={<Verktyg />} />
                  <Route path="/verktyg/brollopsbudget-kalkylator" element={<BudgetCalculator />} />
                  <Route path="/verktyg/brollop-checklista-pdf" element={<ChecklistPage />} />
                  <Route path="/verktyg/gastlista-mall" element={<GuestlistPage />} />
                  <Route path="/verktyg/tidslinje-brollopsdagen" element={<SchemaPage />} />
                  <Route path="/verktyg/bordsplacering-mall" element={<BordsplaceringPage />} />
                  {/* Redirects for old verktyg URLs */}
                  <Route path="/verktyg/bordsplacering-brollop" element={<Navigate to="/verktyg/bordsplacering-mall" replace />} />
                  <Route path="/verktyg/korschema-brollop" element={<Navigate to="/verktyg/tidslinje-brollopsdagen" replace />} />
                  <Route path="/verktyg/sparade-brollopsfavoriter" element={<SavedFavoritesPage />} />
                  <Route path="/profile-settings" element={<ProfileSettings />} />
                  <Route path="/gift/claim" element={<GiftClaim />} />
                  <Route path="/inspiration/:categorySlug/:slug" element={<BlogPost />} />
                  <Route path="/anslut-ditt-foretag/foretagsartiklar" element={<SupplierArticles />} />
                  <Route path="/anslut-ditt-foretag/foretagsartiklar/:categorySlug/:slug" element={<SupplierArticlePost />} />
                  {/* Redirect old /foretagsartiklar to nested path */}
                  <Route path="/foretagsartiklar" element={<ForetagsartiklarListRedirect />} />
                  <Route path="/foretagsartiklar/:categorySlug/:slug" element={<ForetagsartiklarPostRedirect />} />
                  {/* Redirect old /artiklar URLs to /inspiration for SEO */}
                  <Route path="/artiklar/:categorySlug/:slug" element={<ArticleRedirect />} />
                  <Route path="/blog/:slug" element={<BlogRedirect />} />
                  <Route path="/authors/:slug" element={<AuthorProfile />} />
                  <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                  <Route path="/cookie-settings" element={<CookieSettings />} />
                  <Route path="/leverantor-villkor" element={<SupplierTerms />} />

                  {/* Admin blog cms */}

                  <Route path="/admin" element={<ProtectedRoute><BlogCms /></ProtectedRoute>} />
                  <Route path="/admin/login" element={<AdminLogin />} />
                  <Route path="/admin/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/admin/dashboard/feature-labels" element={<ProtectedRoute><FeatureLabelsPage /></ProtectedRoute>} />
                  <Route path="/admin/marketing" element={<ProtectedRoute><MarketingPage /></ProtectedRoute>} />
                  <Route path="/admin/supplier-pricing" element={<ProtectedRoute><SupplierPricingPage /></ProtectedRoute>} />

                  <Route path="/admin/redirects" element={<ProtectedRoute><AdminRedirects /></ProtectedRoute>} />
                  <Route path="/admin/posts" element={<ProtectedRoute><AdminPosts /></ProtectedRoute>} />
                  <Route path="/admin/posts/new" element={<ProtectedRoute><PostEditor /></ProtectedRoute>} />
                  <Route path="/admin/posts/supplier" element={<ProtectedRoute><AdminPosts /></ProtectedRoute>} />
                  <Route path="/admin/posts/supplier/new" element={<ProtectedRoute><PostEditor /></ProtectedRoute>} />
                  <Route path="/admin/posts/supplier/:id" element={<ProtectedRoute><PostEditor /></ProtectedRoute>} />
                  <Route path="/admin/posts/:id" element={<ProtectedRoute><PostEditor /></ProtectedRoute>} />
                  <Route path="/admin/archive" element={<ProtectedRoute><AdminArchive /></ProtectedRoute>} />
                  <Route path="/admin/pagelocations" element={<ProtectedRoute><AdminPagelocations /></ProtectedRoute>} />
                  <Route path="/admin/categories" element={<ProtectedRoute><AdminCategories /></ProtectedRoute>} />
                  <Route path="/admin/tags" element={<ProtectedRoute><AdminTags /></ProtectedRoute>} />
                  <Route path="/admin/supplier-taxonomy" element={<ProtectedRoute><SupplierTaxonomy /></ProtectedRoute>} />
                  <Route path="/admin/authors" element={<ProtectedRoute><AdminAuthors /></ProtectedRoute>} />
                  <Route path="/admin/media" element={<ProtectedRoute><AdminMedia /></ProtectedRoute>} />
                  <Route path="/admin/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/admin/cta-defaults" element={<ProtectedRoute><CtaBlockDefaults /></ProtectedRoute>} />
                  <Route path="/admin/checklist" element={<ProtectedRoute><AdminChecklistTemplates /></ProtectedRoute>} />

                  {/* Planner V2 - Development Playground (admin-only) */}
                  <Route path="/admin/dashboard/planner-v2" element={<ProtectedRoute><PlannerV2Layout /></ProtectedRoute>}>
                    <Route index element={<PlannerV2Dashboard />} />
                    <Route path="checklist" element={<PlannerV2Checklist />} />
                    <Route path="schema" element={<PlannerV2Schema />} />
                    <Route path="budget" element={<PlannerV2Budget />} />
                    <Route path="guestlist" element={<PlannerV2Guestlist />} />
                    <Route path="seating" element={<PlannerV2Seating />} />
                    <Route path="dietary" element={<PlannerV2Dietary />} />
                    <Route path="communication" element={<PlannerV2Communication />} />
                    <Route path="chat" element={<PlannerV2Chat />} />
                    <Route path="wedding-page" element={<PlannerV2WeddingPage />} />
                    <Route path="guest-app" element={<PlannerV2GuestApp />} />
                    <Route path="menu" element={<PlannerV2Menu />} />
                    <Route path="gifts" element={<PlannerV2Gifts />} />
                    <Route path="wishlist" element={<PlannerV2Wishlist />} />
                    <Route path="moodboard" element={<PlannerV2Moodboard />} />
                    <Route path="ai-consultant" element={<PlannerV2AiConsultant />} />
                    <Route path="vendor-catalog" element={<PlannerV2VendorCatalog />} />
                    <Route path="my-vendors" element={<PlannerV2MyVendors />} />
                    <Route path="report" element={<PlannerV2Report />} />
                    <Route path="onboarding" element={<PlannerV2Onboarding />} />
                    <Route path="settings" element={<PlannerV2Settings />} />
                  </Route>

                  {/* Planner */}
                  <Route path="/planner" element={<PlannerLayout />}>
                    <Route index element={<WeddingDashboard />} /> {/* Matches exactly / */}
                    <Route path="profile" element={<Profile />} />
                    <Route path="checklist" element={<Checklist />} />
                    <Route path="budget" element={<Budget />} />
                    <Route path="guestlist" element={<Guestlist />} />
                    <Route path="seating" element={<Seating />} />
                    <Route path="rooms" element={<RoomsPage />} />
                    <Route path="schema" element={<Schema />} />
                    <Route path="saved" element={<Saved />} />
                    <Route path="leverantorer" element={<Leverantorer />} />
                    <Route path="chat" element={<Chat />} />
                  </Route>

                  {/* Embed routes (no auth required, no layout shell) */}
                  <Route path="/embed/seating/:token" element={<EmbedSeating />} />
                  <Route path="/embed/supplier-seating/:supplierPageId" element={<SupplierEmbedSeating />} />
                  <Route path="/embed/demo-seating" element={<DemoSeatingEmbed />} />

                  {/* Public Wedding Page (V2 — no auth, standalone) */}
                  <Route path="/w/:slug" element={<PublicWeddingPage />} />

                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </BrowserRouter>
          </TooltipProvider>
        </CookieConsentProvider>
      </AuthProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
